import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CloseCircleOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Input, message, Modal, Row, Spin, Tooltip } from 'antd';
import dayjs from 'dayjs';
import debounce from 'lodash.debounce';
import { useActivate, useAliveController } from 'react-activation';
import api from '@/api/tasker';
import Pagination from '@/components/pagination';
import eyeIcon from '@/assets/images/tasker-approval-view.png';
import rejectIcon from '@/assets/images/tasker-approval-reject.png';
import approveIcon from '@/assets/images/tasker-approval-approved.png';
import { calendarIcon, searchIcon } from '@/constants/static';
import { disabledDate } from '@/utils/publicMethod';
import format from '@/utils/format';

const ACTION_ICON_SIZE = 24;

const rejectModalCloseIcon = <CloseCircleOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />;

interface ApprovalActionIconProps {
    src: string;
    title: string;
    onClick: () => void;
}

const ApprovalActionIcon = ({ src, title, onClick }: ApprovalActionIconProps) => {
    // Controlled so that using the icon closes its tooltip. "View details" navigates away while KeepAlive keeps
    // this page mounted, so the tooltip never gets a mouse-leave and stayed painted over Tasker Details.
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const activate = () => {
        setTooltipOpen(false);
        onClick();
    };
    return (
        <Tooltip title={title} open={tooltipOpen} onOpenChange={setTooltipOpen}>
            <img
                role="button"
                tabIndex={0}
                src={src}
                alt={title}
                className="approval-action-icon"
                style={{ width: ACTION_ICON_SIZE, height: ACTION_ICON_SIZE, cursor: 'pointer' }}
                onClick={e => {
                    e.stopPropagation();
                    activate();
                }}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        activate();
                    }
                }}
            />
        </Tooltip>
    );
};

type ApprovalTab = 'pending' | 'approved' | 'rejected';

interface ListParams {
    pageNum: number;
    pageSize: number;
    startTime: string;
    endTime: string;
    keyword: string;
    approvalStatus: ApprovalTab;
}

interface ApprovalTasker {
    taskerId: string;
    fullName: string;
    email: string;
    mobile: string;
    areaCode?: string;
    gender: number;
    createTime: string;
    approvalStatus: ApprovalTab;
    approvalReviewedTime?: string;
    approvalRejectionReason?: string;
    street?: string;
    suburb?: string;
    state?: string;
    postcode?: string;
    /** Applied on the public website (no password until approved) rather than signing up in the app. */
    fromWebsite?: boolean;
}

const TAB_ITEMS = [
    { key: 'pending', label: 'Pending approval' },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' },
] as const;

const statusLabel: Record<ApprovalTab, string> = {
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
};

/** Ant Design row columns must sum to 24; actions column is always last */
const TABLE_COL_SPANS: Record<
    ApprovalTab,
    {
        fullName: number;
        email: number;
        mobile: number;
        state: number;
        source: number;
        registered: number;
        status: number;
        rejection?: number;
        actions: number;
    }
> = {
    pending: { fullName: 4, email: 4, mobile: 3, state: 2, source: 2, registered: 3, status: 2, actions: 4 },
    approved: { fullName: 4, email: 5, mobile: 3, state: 2, source: 2, registered: 3, status: 2, actions: 3 },
    rejected: {
        fullName: 3,
        email: 4,
        mobile: 3,
        state: 2,
        source: 2,
        registered: 2,
        status: 2,
        rejection: 3,
        actions: 3,
    },
};

const renderActionsToolbar = (
    approvalTab: ApprovalTab,
    handlers: {
        onView: () => void;
        onApprove: () => void;
        onReject: () => void;
    }
) => (
    <div className="approval-actions-toolbar">
        <ApprovalActionIcon src={eyeIcon} title="View details" onClick={handlers.onView} />
        {approvalTab === 'pending' && (
            <>
                <ApprovalActionIcon src={approveIcon} title="Approve" onClick={handlers.onApprove} />
                <ApprovalActionIcon src={rejectIcon} title="Reject" onClick={handlers.onReject} />
            </>
        )}
        {approvalTab === 'rejected' && (
            <ApprovalActionIcon src={approveIcon} title="Approve" onClick={handlers.onApprove} />
        )}
    </div>
);

const ApproveTasker = () => {
    const link = useNavigate();
    const { drop } = useAliveController();
    const { RangePicker } = DatePicker;
    const [loading, setLoading] = useState(false);
    const [totalRecord, setTotalRecord] = useState(0);
    const [tableData, setTableData] = useState<ApprovalTasker[]>([]);
    const [confirmModal, setConfirmModal] = useState<{
        open: boolean;
        taskerId: string;
        fullName: string;
        action: 'approved' | 'rejected';
        fromWebsite?: boolean;
    }>({ open: false, taskerId: '', fullName: '', action: 'approved' });
    const [rejectionReason, setRejectionReason] = useState('');
    const [listParams, setListParams] = useState<ListParams>({
        pageNum: 1,
        pageSize: 10,
        startTime: '',
        endTime: '',
        keyword: '',
        approvalStatus: 'pending',
    });
    const [tabCounts, setTabCounts] = useState<Record<ApprovalTab, number>>({
        pending: 0,
        approved: 0,
        rejected: 0,
    });

    const fetchTabCounts = async (filters: Pick<ListParams, 'keyword' | 'startTime' | 'endTime'>) => {
        const base = { pageNum: 1, pageSize: 1, ...filters };
        const statuses: ApprovalTab[] = ['pending', 'approved', 'rejected'];
        try {
            const results = await Promise.all(
                statuses.map(approvalStatus => api.getTaskerApprovalList({ ...base, approvalStatus }))
            );
            setTabCounts({
                pending: Number(results[0].data?.total ?? 0),
                approved: Number(results[1].data?.total ?? 0),
                rejected: Number(results[2].data?.total ?? 0),
            });
        } catch {
            // keep previous counts on failure
        }
    };

    const getList = async (params: ListParams) => {
        setLoading(true);
        try {
            const res = await api.getTaskerApprovalList(params);
            const records = (res.data?.records ?? []).map((item: ApprovalTasker) => ({
                ...item,
                taskerId: String(item.taskerId),
                mobile: item.mobile ? window.utils.formatMobileNumber(item.mobile) : '-',
            }));
            setTableData(records);
            setTotalRecord(Number(res.data?.total ?? 0));
        } catch (error: any) {
            message.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getList(listParams);
    }, [listParams]);

    useEffect(() => {
        fetchTabCounts({
            keyword: listParams.keyword,
            startTime: listParams.startTime,
            endTime: listParams.endTime,
        });
    }, [listParams.keyword, listParams.startTime, listParams.endTime]);

    const reloadListAndCounts = () => {
        getList(listParams);
        fetchTabCounts({
            keyword: listParams.keyword,
            startTime: listParams.startTime,
            endTime: listParams.endTime,
        });
    };

    // This page is KeepAlive-cached, so coming back to it (e.g. from Tasker Details, where the tasker may
    // have just been approved or rejected) would otherwise show the old rows and counts. Acting on a stale
    // "Pending" row is what produced the approve error. Not fired on first mount, so nothing loads twice.
    useActivate(reloadListAndCounts);

    const debouncedKeyword = useCallback(
        debounce((value: string) => {
            setListParams(state => ({ ...state, keyword: value, pageNum: 1 }));
        }, 500),
        []
    );

    const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
        debouncedKeyword(event.target.value);
    };

    const handlePageChange = (pageNo: number) => {
        setListParams(state => ({ ...state, pageNum: pageNo }));
    };

    const handleTabChange = (tab: ApprovalTab) => {
        setListParams(state => ({ ...state, approvalStatus: tab, pageNum: 1 }));
    };

    const approvalTab = listParams.approvalStatus;

    const dateOnChange = (date: any) => {
        setListParams({
            ...listParams,
            pageNum: 1,
            startTime: date ? dayjs(`${dayjs(date[0]).format('YYYY-MM-DD')} 00:00:00`).toISOString() : '',
            endTime: date ? dayjs(`${dayjs(date[1]).format('YYYY-MM-DD')} 23:59:59`).toISOString() : '',
        });
    };

    const openConfirm = (item: ApprovalTasker, action: 'approved' | 'rejected') => {
        setRejectionReason('');
        setConfirmModal({
            open: true,
            taskerId: item.taskerId,
            fullName: item.fullName,
            action,
            fromWebsite: item.fromWebsite,
        });
    };

    const closeConfirm = () => {
        setConfirmModal({ open: false, taskerId: '', fullName: '', action: 'approved' });
        setRejectionReason('');
    };

    const submitApproval = async () => {
        const { taskerId, action } = confirmModal;
        const reason = rejectionReason.trim();
        if (action === 'rejected' && !reason) {
            message.warning('Please enter a rejection reason');
            return;
        }
        try {
            await api.updateTaskerApproval(taskerId, action, action === 'rejected' ? reason : undefined);
            message.success(
                action === 'rejected'
                    ? 'Tasker rejected'
                    : confirmModal.fromWebsite
                    ? 'Tasker approved. They have been emailed a link to set their password.'
                    : 'Tasker approved'
            );
            closeConfirm();
            reloadListAndCounts();
        } catch (error: any) {
            message.error(error);
            // The row may be out of date (decided elsewhere meanwhile); reload so it shows where it really is.
            reloadListAndCounts();
        }
    };

    const goToDetail = async (item: ApprovalTasker) => {
        window.sessionStorage.setItem('taskerInfo', JSON.stringify(item));
        // Tasker Details is KeepAlive-cached and reads taskerInfo only when it mounts, so a cached copy kept
        // showing whichever tasker was opened first. Drop it (as the Tasker list does) so this one loads.
        await drop('TaskerDateil');
        link('/tasker/detail?fromApproval=1');
    };

    const popupContainer = document.getElementById('approve-tasker-bar') || document.body;

    return (
        <div className="page-main account">
            <div className="page-title">Approve Tasker</div>
            <div className="select-bar" id="approve-tasker-bar">
                <div className="select-bar-left">
                    <RangePicker
                        style={{ width: 'max-content' }}
                        className="search-input"
                        placeholder={['dd/mm/yyyy', 'dd/mm/yyyy']}
                        suffixIcon={calendarIcon}
                        separator="-"
                        format="DD/MM/YYYY"
                        disabledDate={disabledDate}
                        onChange={dateOnChange}
                        getPopupContainer={() => popupContainer}
                    />
                    <Input
                        className="search-input"
                        style={{ maxWidth: 220 }}
                        placeholder="Search name, email, mobile"
                        onChange={handleSearch}
                        prefix={searchIcon}
                    />
                </div>
            </div>
            <div className="table approve-tasker-table">
                <div className="tabs-table">
                    {TAB_ITEMS.map(tab => (
                        <div
                            key={tab.key}
                            role="tab"
                            tabIndex={0}
                            aria-selected={approvalTab === tab.key}
                            className={
                                approvalTab === tab.key ? 'tabs-table-item tabs-table-item-active' : 'tabs-table-item'
                            }
                            onClick={() => handleTabChange(tab.key)}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleTabChange(tab.key);
                                }
                            }}
                        >
                            {tab.label} ({tabCounts[tab.key]})
                        </div>
                    ))}
                </div>
                {loading ? (
                    <div className="data_loading flex-center">
                        <Spin />
                    </div>
                ) : totalRecord ? (
                    <>
                        <div className="table-th">
                            <Row wrap={false}>
                                {(() => {
                                    const spans = TABLE_COL_SPANS[approvalTab];
                                    return (
                                        <>
                                            <Col span={spans.fullName}>Full Name</Col>
                                            <Col span={spans.email}>Email</Col>
                                            <Col span={spans.mobile}>Mobile</Col>
                                            <Col span={spans.state}>State</Col>
                                            <Col span={spans.source}>Source</Col>
                                            <Col span={spans.registered}>Registered</Col>
                                            <Col span={spans.status}>Status</Col>
                                            {spans.rejection != null && (
                                                <Col span={spans.rejection}>Rejection reason</Col>
                                            )}
                                            <Col span={spans.actions}>Actions</Col>
                                        </>
                                    );
                                })()}
                            </Row>
                        </div>
                        {tableData.map((item, index) => (
                            <Row className="table-tr" wrap={false} key={`approval-${item.taskerId}-${index}`}>
                                {(() => {
                                    const spans = TABLE_COL_SPANS[approvalTab];
                                    return (
                                        <>
                                            <Col span={spans.fullName} title={item.fullName}>
                                                {item.fullName || '-'}
                                            </Col>
                                            <Col span={spans.email}>{item.email ?? '-'}</Col>
                                            <Col span={spans.mobile}>{item.mobile}</Col>
                                            <Col span={spans.state}>{item.state ?? '-'}</Col>
                                            <Col span={spans.source}>{item.fromWebsite ? 'Website' : 'App'}</Col>
                                            <Col span={spans.registered}>{format.formatTimeMDY(item.createTime)}</Col>
                                            <Col span={spans.status}>
                                                {statusLabel[item.approvalStatus] ?? item.approvalStatus}
                                            </Col>
                                            {spans.rejection != null && (
                                                <Col span={spans.rejection} title={item.approvalRejectionReason}>
                                                    {item.approvalRejectionReason || '-'}
                                                </Col>
                                            )}
                                            <Col span={spans.actions}>
                                                {renderActionsToolbar(approvalTab, {
                                                    onView: () => goToDetail(item),
                                                    onApprove: () => openConfirm(item, 'approved'),
                                                    onReject: () => openConfirm(item, 'rejected'),
                                                })}
                                            </Col>
                                        </>
                                    );
                                })()}
                            </Row>
                        ))}
                        <Pagination total={totalRecord} pageChange={handlePageChange} pageNo={listParams.pageNum} />
                    </>
                ) : (
                    <div className="no-data flex-center">No taskers found</div>
                )}
            </div>
            <Modal
                width={560}
                maskClosable={false}
                centered
                open={confirmModal.open}
                onCancel={closeConfirm}
                closeIcon={confirmModal.action === 'rejected' ? rejectModalCloseIcon : null}
                footer={null}
            >
                <div className="modal-title">
                    {confirmModal.action === 'approved' ? 'Approve tasker' : 'Reject tasker'}
                </div>
                <div className="modal-tip tip-font-20">
                    {confirmModal.action === 'approved' ? (
                        <>
                            Are you sure you want to approve <strong>{confirmModal.fullName}</strong>?{' '}
                            {confirmModal.fromWebsite
                                ? 'They applied on the website, so we will email them a link to set their password in the tasker app.'
                                : 'They will be able to use the tasker app.'}
                        </>
                    ) : (
                        <>Please provide a reason for rejecting {confirmModal.fullName}.</>
                    )}
                </div>
                {confirmModal.action === 'rejected' && (
                    <Input.TextArea
                        style={{ marginTop: 16 }}
                        rows={4}
                        maxLength={1000}
                        showCount
                        placeholder="Enter rejection reason (required)"
                        value={rejectionReason}
                        onChange={e => setRejectionReason(e.target.value)}
                    />
                )}
                <div className="flex-center" style={{ marginTop: 30, columnGap: 20 }}>
                    <Button className="submit-btn" onClick={closeConfirm}>
                        Cancel
                    </Button>
                    <Button
                        className="submit-btn"
                        type="primary"
                        danger={confirmModal.action === 'rejected'}
                        onClick={submitApproval}
                    >
                        {confirmModal.action === 'approved' ? 'Approve' : 'Reject'}
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default ApproveTasker;
